import { Box, Typography } from "@mui/material";
import React, { useEffect } from "react";

const Ruler = React.memo(({ width, height, showRuler, baseScale, children }) => {
  // Debug re-renders
  useEffect(() => {
    console.log("Ruler: Re-rendered", { width, height, showRuler, baseScale });
  }, [width, height, showRuler, baseScale]);

  // Fallback baseScale if not provided
  const pixelsPerInch = baseScale || 1;
  const pixelsPerFoot = pixelsPerInch * 12;

  // Calculate canvas dimensions in pixels
  const canvasWidthPx = width * pixelsPerInch;
  const canvasHeightPx = height * pixelsPerInch;

  const generateRulerTicks = (size, isVertical) => {
    if (!size) return [];
    const useFeet = size >= 48;
    const unitSize = useFeet ? size / 12 : size; // Size in feet or inches
    const unitLabel = useFeet ? "ft" : "in";
    const majorInterval = useFeet ? 1 : 12; // Major ticks: 1ft or 12in
    const minorInterval = useFeet ? 0.1 : 1; // Minor ticks: 0.1ft or 1in
    const ticks = [];
    const numTicks = Math.ceil(unitSize / minorInterval);

    // Total pixel length of the ruler
    const totalPx = useFeet
      ? (size * pixelsPerFoot) / 12
      : size * pixelsPerInch;

    for (let i = 0; i <= numTicks; i++) {
      const value = i * minorInterval;
      if (value > unitSize) break;
      const isMajor = value % majorInterval === 0;

      // Calculate position in pixels
      const pixelPosition = useFeet
        ? value * pixelsPerFoot // Position in pixels for feet
        : value * pixelsPerInch; // Position in pixels for inches

      // Convert to percentage for CSS positioning
      const positionPercent = (pixelPosition / totalPx) * 100;

      ticks.push({
        value: useFeet ? value.toFixed(1) : Math.round(value),
        isMajor,
        position: positionPercent,
        pixelPosition, // For logging
        label:
          isMajor && value !== 0
            ? `${useFeet ? value.toFixed(1) : value} ${unitLabel}`
            : null,
      });
    }
/*
    console.log(
      `Ruler: Generated ticks (${isVertical ? "vertical" : "horizontal"})`,
      {
        size,
        useFeet,
        unitSize,
        totalPx,
        pixelsPerInch,
        pixelsPerFoot,
        ticks: ticks.map((tick) => ({
          value: tick.value,
          pixelPosition: tick.pixelPosition,
          positionPercent: tick.position,
          label: tick.label,
        })),
      }
    );
    */

    return ticks;
  };

  const widthTicks = generateRulerTicks(width, false);
  const heightTicks = generateRulerTicks(height, true);
  const cornerUnit = width >= 48 ? "0.0 ft" : "0 in";

  return (
    <Box
      sx={{
        position: "relative",
        maxWidth: "100%",
        display: "grid",
        gridTemplateColumns: showRuler ? "40px 1fr" : "0px 1fr",
        gridTemplateRows: showRuler ? "48px 1fr" : "0px 1fr",
        gap: 0,
      }}
    >
      {/* Top-left corner */}
      <Box
        sx={{
          display: showRuler ? "flex" : "none",
          backgroundColor: "#e0e0e0",
          justifyContent: "center",
          alignItems: "center",
          gridColumn: "1 / 2",
          gridRow: "1 / 2",
          pointerEvents: "none",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px", color: "#333" }}>
          {cornerUnit}
        </Typography>
      </Box>
      {/* Horizontal ruler (top) */}
      <Box
        sx={{
          display: showRuler ? "block" : "none",
          backgroundColor: "#e0e0e0",
          height: "48px",
          width: canvasWidthPx,
          position: "relative",
          borderBottom: "1px solid #666",
          paddingLeft: 1,
          gridColumn: "2 / 3",
          gridRow: "1 / 2",
          pointerEvents: "none",
        }}
      >
        {widthTicks.map((tick, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              left: `${tick.position}%`,
              transform: "translateX(-50%)",
              height: tick.isMajor ? "12px" : "6px",
              width: "1px",
              backgroundColor: "#666",
              bottom: 0,
            }}
          >
            {tick.label && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: "14px",
                  transform: "translateX(-50%)",
                  fontSize: "10px",
                  color: "#333",
                  whiteSpace: "nowrap",
                }}
              >
                {tick.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      {/* Vertical ruler (left) */}
      <Box
        sx={{
          display: showRuler ? "flex" : "none",
          backgroundColor: "#e0e0e0",
          width: "40px",
          height: canvasHeightPx,
          position: "relative",
          justifyContent: "flex-end",
          borderRight: "1px solid #666",
          gridColumn: "1 / 2",
          gridRow: "2 / 3",
          zIndex: 10,
          pointerEvents: "none",
        }}
      >
        {heightTicks.map((tick, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              top: `${tick.position}%`,
              transform: "translateY(-50%)",
              width: tick.isMajor ? "12px" : "6px",
              height: "1px",
              backgroundColor: "#666",
              right: 0,
            }}
          >
            {tick.label && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  right: "14px",
                  transform: "translateY(-50%)",
                  fontSize: "10px",
                  color: "#333",
                }}
              >
                {tick.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      {/* Canvas area */}
      <Box
        sx={{
          gridColumn: "2 / 3",
          gridRow: "2 / 3",
          position: "relative",
          width: `${canvasWidthPx}px`,
          height: `${canvasHeightPx}px`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
});

export default Ruler;